import { useEffect } from "react";
import { PayPalButtons } from "@paypal/react-paypal-js";

/**
 * Renders PayPal Smart Payment Buttons for a pre-created reservation.
 * Must be rendered inside a parent PayPalScriptProvider.
 *
 * Props:
 *   reservationId  — the reservation to capture against
 *   paypalOrderId  — the PayPal order ID returned by createReservation
 *   onSuccess(reservationId) — called after successful capture
 *   onError(message)         — called on failure
 */
export default function PayPalCheckoutButton({ reservationId, paypalOrderId, onSuccess, onError }) {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  useEffect(() => {
    const suppress = (event) => {
      if (event.message === "Script error." || event.message === "ResizeObserver loop limit exceeded") {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    window.addEventListener("error", suppress);
    return () => window.removeEventListener("error", suppress);
  }, []);

  return (
    <PayPalButtons
      style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay", height: 50 }}
      createOrder={() => paypalOrderId}
      onApprove={async () => {
        try {
          const res = await fetch(
            `${apiUrl}/ticketing/reservations/${reservationId}/paypal-capture`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ order_id: paypalOrderId }),
            }
          );
          const data = await res.json();
          if (!res.ok) throw new Error(data.detail || "Capture failed");
          onSuccess(reservationId);
        } catch (err) {
          onError(err.message || "PayPal payment failed. Please try again.");
        }
      }}
      onError={(err) => {
        onError("PayPal encountered an error. Please try again.");
        console.error("PayPal error:", err);
      }}
      onCancel={() => onError("")}
    />
  );
}
